
const TOKEN_KEY = 'saf:accessToken';
const OAUTH_PENDING_KEY = 'saf:oauthPending';
const LAST_EXPIRED_KEY = 'saf:lastTokenExpired';
export const EXPIRATION_SKEW_SECONDS = 60;
const REFRESH_THRESHOLD_SECONDS = 2 * 24 * 60 * 60; // 2 days

let accessToken: string | null = localStorage.getItem(TOKEN_KEY);

type JwtPayload = {
  exp?: number;
  iat?: number;
  name?: string;
  role?: string | string[];
  roles?: string | string[];
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'?: string | string[];
  email?: string;
  Email?: string;
  preferred_username?: string;
  hasLicense?: string | boolean;
  hadLicenseInPast?: string | boolean;
  [key: string]: unknown;
};

function decodeSegment(segment: string): string {
  return atob(segment.replace(/-/g, '+').replace(/_/g, '/'));
}

function parseTokenPayload(token: string): JwtPayload | null {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const payloadJson = decodeSegment(parts[1]);
    return JSON.parse(payloadJson) as JwtPayload;
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function isLoggedIn(): boolean {
  return Boolean(accessToken);
}

export function isTokenExpired(token: string, skewSeconds = 0): boolean {
  const payload = parseTokenPayload(token);
  const expSeconds = payload?.exp;
  if (typeof expSeconds !== 'number') {
    return true;
  }

  const expiresAtMs = expSeconds * 1000;
  const nowMs = Date.now();
  const skewMs = skewSeconds * 1000;
  return nowMs >= expiresAtMs - skewMs;
}

export function secondsUntilExpiry(token: string): number | null {
  const payload = parseTokenPayload(token);
  const expSeconds = payload?.exp;
  if (typeof expSeconds !== 'number') {
    return null;
  }
  return expSeconds - Math.floor(Date.now() / 1000);
}

export function shouldRefreshToken(token: string, thresholdSeconds = REFRESH_THRESHOLD_SECONDS): boolean {
  const secondsLeft = secondsUntilExpiry(token);
  return secondsLeft !== null && secondsLeft < thresholdSeconds;
}

export function setLastTokenExpired(isExpired: boolean) {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    if (isExpired) {
      window.sessionStorage.setItem(LAST_EXPIRED_KEY, '1');
    } else {
      window.sessionStorage.removeItem(LAST_EXPIRED_KEY);
    }
  } catch {
  }
}

export function wasLastTokenExpired(): boolean {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return false;
    }
    return window.sessionStorage.getItem(LAST_EXPIRED_KEY) === '1';
  } catch {
    return false;
  }
}

type AuthChangeListener = () => void;
const authListeners = new Set<AuthChangeListener>();

export function notifyAuthListeners() {
  authListeners.forEach((listener) => {
    try {
      listener();
    } catch {
    }
  });
}

export function subscribeToAuthChanges(listener: AuthChangeListener): () => void {
  authListeners.add(listener);
  return () => {
    authListeners.delete(listener);
  };
}

export function setAccessToken(token: string | null, options?: { expired?: boolean }) {
  accessToken = token;
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    setLastTokenExpired(false);
  } else {
    localStorage.removeItem(TOKEN_KEY);
    if (options?.expired) {
      setLastTokenExpired(true);
    } else {
      setLastTokenExpired(false);
    }
  }
  notifyAuthListeners();
}

export function setPendingOAuthToken(token: string | null) {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    if (token) {
      window.sessionStorage.setItem(OAUTH_PENDING_KEY, token);
    } else {
      window.sessionStorage.removeItem(OAUTH_PENDING_KEY);
    }
  } catch {
    /* ignore storage failures */
  }
}

export function getPendingOAuthToken(): string | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }
    return window.sessionStorage.getItem(OAUTH_PENDING_KEY);
  } catch {
    return null;
  }
}

export function getUserEmail(): string | null {
  if (!accessToken) return null;
  const payload = parseTokenPayload(accessToken);
  const emailClaim = payload?.email ?? payload?.Email ?? payload?.preferred_username;
  return typeof emailClaim === 'string' ? emailClaim : null;
}

export function getUserName(): string | null {
  if (!accessToken) return null;
  const payload = parseTokenPayload(accessToken);
  return payload?.name ?? null;
}

export function hasLicense(): boolean {
  if (!accessToken) return false;
  const payload = parseTokenPayload(accessToken);
  return payload?.hasLicense === 'true' || payload?.hasLicense === true;
}

export function hadLicenseInPast(): boolean {
  if (!accessToken) return false;
  const payload = parseTokenPayload(accessToken);
  return payload?.hadLicenseInPast === 'true' || payload?.hadLicenseInPast === true;
}

export function getUserRoles(): string[] {
  if (!accessToken) return [];
  const payload = parseTokenPayload(accessToken);
  const raw =
    payload?.role ??
    payload?.roles ??
    payload?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.filter((r): r is string => typeof r === 'string');
  }

  if (typeof raw === 'string') {
    return [raw];
  }

  return [];
}

export function isBlogAdmin(): boolean {
  return getUserRoles().some((role) => role.toLowerCase() === 'blogadmin');
}

// Initial check
if (accessToken && isTokenExpired(accessToken)) {
  setAccessToken(null, { expired: true });
}
